
import { useCallback, useRef } from 'react';

interface UseThrottlingDebounceProps {
  fetchInProgressRef: React.MutableRefObject<boolean>;
  debounceTimeoutRef: React.MutableRefObject<number | null>;
  lastFetchTimeRef: React.MutableRefObject<number>;
  FETCH_THROTTLE_MS?: number;
}

export function useThrottlingDebounce({
  fetchInProgressRef,
  debounceTimeoutRef,
  lastFetchTimeRef,
  FETCH_THROTTLE_MS = 3000
}: UseThrottlingDebounceProps) {
  
  // Clean up debounce and other timeouts
  const cleanupTimeouts = useCallback(() => {
    if (debounceTimeoutRef.current) {
      window.clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, [debounceTimeoutRef]);
  
  // Check if we should throttle the request
  const shouldThrottleRequest = useCallback(() => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;
    
    return timeSinceLastFetch < FETCH_THROTTLE_MS && lastFetchTimeRef.current > 0;
  }, [lastFetchTimeRef, FETCH_THROTTLE_MS]);

  // Check if a fetch is already in progress
  const isFetchInProgress = useCallback(() => {
    return fetchInProgressRef.current;
  }, [fetchInProgressRef]);
  
  // Update the last fetch time
  const updateLastFetchTime = useCallback(() => {
    lastFetchTimeRef.current = Date.now();
  }, [lastFetchTimeRef]);

  return {
    cleanupTimeouts,
    shouldThrottleRequest,
    isFetchInProgress,
    updateLastFetchTime
  };
}
